"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@west-tennessee-consulting/ui";
import { CentralizedDrawer } from "@west-tennessee-consulting/ui";
import { createEnhancedGenericForm } from "@/components/shared/EnhancedGenericCreateForm";
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { Contacts } from "@/src/gql/graphql";

const ENHANCED_CONTACTS_CONFIG = getEntityTableConfig<Contacts>("contacts");
import { MatchContactDrawer } from "../match/MatchContactDrawer";
import { MatchQBCustomerDrawer } from "../match/MatchQBCustomerDrawer";
import { useCreateContact } from "@/features/contacts/hooks";
import { useMatchQBCustomer } from "@/lib/hooks/api/admin";
import { parseQuickBooksCustomerRef } from "@/lib/utils/crm/quickbooks-company-ref";
import type { ContactsInsertInput } from "@/src/gql/graphql";

interface QuickBooksActionsProps {
  qbCustomerId?: string; // QuickBooks CustomerRef (preferred)
  qbDisplayName?: string; // QuickBooks display name (for labeling)
  contactId?: string; // Current contact id (for single match)
  companyId?: string; // Company ID for company-level operations
  companyName?: string; // Company for bulk match
  className?: string;
}

export const QuickBooksActions: React.FC<QuickBooksActionsProps> = ({
  qbCustomerId,
  qbDisplayName,
  contactId,
  companyId,
  companyName,
  className,
}) => {
  const matchMutation = useMatchQBCustomer();
  const createContact = useCreateContact();

  // Parse multiple customer references from the qbCustomerId field
  const customerRefs = React.useMemo(() => {
    return parseQuickBooksCustomerRef(qbCustomerId);
  }, [qbCustomerId]);

  const [isMatchOpen, setIsMatchOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isQBDetailsOpen, setIsQBDetailsOpen] = useState(false);
  const [isMatchQBCustomerOpen, setIsMatchQBCustomerOpen] = useState(false);

  const handleConfirmMatch = async (selectedContactId: string) => {
    if (!qbCustomerId || !companyId) return;
    await matchMutation.mutateAsync({
      qbId: qbCustomerId,
      contactId: selectedContactId,
      quickbooksDisplayName: qbDisplayName,
      quickbooksRealmId: undefined,
    });
    setIsMatchOpen(false);
  };

  const handleCreateAndMatch = async (formData: Partial<Contacts>) => {
    const created = await createContact.mutateAsync({
      ...(formData as ContactsInsertInput),
      status: formData?.status || "customer",
    });
    if (created?.id && qbCustomerId && companyId) {
      await matchMutation.mutateAsync({
        qbId: qbCustomerId,
        contactId: created.id,
        quickbooksDisplayName: qbDisplayName,
        quickbooksRealmId: undefined,
      });
    }
    setIsCreateOpen(false);
  };

  const handleBulkMatchCompany = async () => {
    if (!qbCustomerId || !companyName) return;
    await matchMutation.mutateAsync({
      qbId: qbCustomerId,
      quickbooksDisplayName: qbDisplayName,
      bulkCompanyName: companyName,
      quickbooksRealmId: undefined,
    });
  };

  // Stable onConfirm — MatchQBCustomerDrawer is memo'd (MEMO_STABLE_PROPS).
  const handleMatchQBCustomerConfirm = React.useCallback(
    async (newQbId: string, newQbName?: string, realmId?: string, _companyId?: string) => {
      if (contactId) {
        await matchMutation.mutateAsync({
          qbId: newQbId,
          contactId,
          quickbooksDisplayName: qbDisplayName ?? newQbName,
          quickbooksRealmId: realmId,
        });
      }
      setIsMatchQBCustomerOpen(false);
    },
    [contactId, matchMutation, qbDisplayName]
  );

  return (
    <div className={`flex flex-wrap gap-2 ${className || ""}`}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsQBDetailsOpen(true)}
              disabled={customerRefs.length === 0}
            >
              View QB {customerRefs.length > 1 ? `(${customerRefs.length})` : ""}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {customerRefs.length > 1
              ? `View details for ${customerRefs.length} QuickBooks customers`
              : "View QuickBooks customer details"}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="default" size="sm" onClick={() => setIsMatchQBCustomerOpen(true)}>
              {customerRefs.length > 0 ? "Add QB Customer" : "Match Company"}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {customerRefs.length > 0
              ? "Add another QuickBooks customer to this contact"
              : "Link this contact to a QuickBooks customer"}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" onClick={() => setIsCreateOpen(true)}>
              Create & Match
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Create a new contact and link it to this QuickBooks customer
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleBulkMatchCompany}
              disabled={!companyName}
            >
              Match All in Company
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {companyName
              ? "Apply this QuickBooks mapping to all contacts with the same company name"
              : "No company on contact to bulk match"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Match to QuickBooks Customer */}
      <MatchQBCustomerDrawer
        open={isMatchQBCustomerOpen}
        onOpenChange={setIsMatchQBCustomerOpen}
        title={qbCustomerId ? "Edit QB Company Match" : "Match Company"}
        description={qbDisplayName || companyName}
        contactId={contactId}
        companyId={companyId}
        onConfirm={handleMatchQBCustomerConfirm}
      />

      {/* Create & Match Drawer */}
      <CentralizedDrawer
        drawerId="mobile-quickbooks-actions"
        side="bottom"
        title="Create Contact & Match"
        description={qbDisplayName || companyName || "QuickBooks Customer"}
        showCloseButton={true}
        enableSwipeGesture={true}
      >
        <div className="flex-1 overflow-y-auto p-4">
          {React.createElement(
            createEnhancedGenericForm<Contacts>("contacts", ENHANCED_CONTACTS_CONFIG),
            {
              onSubmit: handleCreateAndMatch,
              initialValues: {
                company_name: companyName || qbDisplayName || "",
                status: "customer",
              },
            }
          )}
        </div>
        <div className="border-t p-4">
          <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
            Close
          </Button>
        </div>
      </CentralizedDrawer>

      {/* QB Details Drawer (minimal) */}
      <CentralizedDrawer
        drawerId="mobile-quickbooks-details"
        side="bottom"
        title="QuickBooks Customers"
        description={
          customerRefs.length > 1
            ? `${customerRefs.length} customers linked`
            : qbDisplayName || "Customer"
        }
        showCloseButton={true}
        enableSwipeGesture={true}
      >
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {customerRefs.length > 0 ? (
              customerRefs.map((ref, index) => (
                <div key={index} className="p-3 border rounded-lg space-y-2">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Customer ID:</span> {ref.customerId}
                  </div>
                  {ref.realmId && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Realm ID:</span> {ref.realmId}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-sm text-muted-foreground">No QuickBooks customers linked</div>
            )}
          </div>
        </div>
        <div className="border-t p-4">
          <Button variant="outline" onClick={() => setIsQBDetailsOpen(false)}>
            Close
          </Button>
        </div>
      </CentralizedDrawer>
    </div>
  );
};
